/**
 * The Australian style engine.
 *
 * One entry point, `applyAustralianStyle`, which returns the corrected text
 * together with every change it made and every thing it noticed but chose to
 * leave alone. Nothing is corrected silently: a doctor signs this document, so
 * the doctor gets to see what the machine did to their words.
 *
 * The rules come from three places, in this order of authority:
 *
 *   1. Beam's own `Guide to report abbreviations`, which is a QA training
 *      document and therefore the house style.
 *   2. The repository rules for this client: long-form dates, no em or en
 *      dashes anywhere.
 *   3. General Australian English usage, for spelling and vocabulary.
 */
import { DEFAULT_OPTIONS, PRIORITY, } from './types.js';
import { applyEdits, OffsetMap, IDENTITY_MAP } from './edits.js';
import { findPunctuation } from './punctuation.js';
import { findDates, findTimes } from './dates.js';
import { findMeasures } from './measures.js';
import { buildVocabularyPattern } from './vocabulary.js';
import { lookupSpelling, matchCase, SPELLING_FLAGS } from './spelling.js';
import { digitStaysNumeric, numberToWord, parseNumberWords, WORD_VALUE } from './numbers.js';
import { findDictationFixes } from './dictation.js';
export * from './types.js';
export * from './punctuation.js';
export * from './dictation.js';
export { formatAuDate, findDates, findTimes, MONTHS } from './dates.js';
export { lookupSpelling, SPELLING_EXCLUDE } from './spelling.js';
export { assembleKeyterms, MAX_KEYTERMS } from './dictation.js';
const WORD = /[A-Za-z][A-Za-z'-]*/g;
// Every single digit that is not part of a longer number. Whether it stays a
// numeral is decided by `digitStaysNumeric`, which can then say why, rather
// than by a regex that silently never matches the interesting cases.
const DIGIT_1_TO_9 = /(?<!\d)\d(?!\d)/g;
const NUMBER_WORD_RUN = new RegExp(String.raw `\b(` + [...WORD_VALUE.keys()].sort((a, b) => b.length - a.length).join('|') +
    String.raw `)(?:\s+(` + [...WORD_VALUE.keys()].sort((a, b) => b.length - a.length).join('|') + String.raw `))?\b`, 'gi');
function collectVocabulary(text, extra) {
    const { pattern, lookup } = buildVocabularyPattern(extra);
    const edits = [];
    for (const m of text.matchAll(pattern)) {
        const matched = m[0];
        const key = matched.toLowerCase().replace(/\s+/g, ' ');
        const entry = lookup.get(key);
        if (!entry)
            continue;
        const replacement = entry.literal ? entry.to : matchCase(matched, entry.to);
        if (replacement === matched)
            continue;
        edits.push({
            start: m.index,
            end: m.index + matched.length,
            replacement,
            rule: `vocabulary:${entry.from}`,
            category: 'vocabulary',
            confidence: entry.confidence ?? 'certain',
            ...(entry.note ? { note: entry.note } : {}),
            priority: PRIORITY.vocabulary,
        });
    }
    return edits;
}
function collectSpelling(text) {
    const edits = [];
    for (const m of text.matchAll(WORD)) {
        const word = m[0];
        const hit = lookupSpelling(word);
        if (!hit)
            continue;
        const replacement = matchCase(word, hit.au);
        if (replacement === word)
            continue;
        edits.push({
            start: m.index,
            end: m.index + word.length,
            replacement,
            rule: `spelling:${word.toLowerCase()}`,
            category: 'spelling',
            confidence: hit.confidence,
            ...(hit.note ? { note: hit.note } : {}),
            priority: PRIORITY.spelling,
        });
    }
    return edits;
}
function collectNumbers(text) {
    const edits = [];
    const flags = [];
    // Digits one to nine become words, unless context says they are a label,
    // a measurement, a range, a fraction, a score or part of a date.
    for (const m of text.matchAll(DIGIT_1_TO_9)) {
        const start = m.index;
        const end = start + m[0].length;
        const digit = m[0];
        if (digit === '0')
            continue;
        const reason = digitStaysNumeric({ text, start, end });
        if (reason) {
            flags.push({
                rule: 'number:kept-numeric',
                category: 'number',
                text: digit,
                start,
                end,
                reason,
            });
            continue;
        }
        const word = numberToWord(Number(digit));
        if (!word)
            continue;
        edits.push({
            start, end,
            replacement: word,
            rule: 'number:digit-to-word',
            category: 'number',
            confidence: 'certain',
            note: "the guide writes one to nine as words",
            priority: PRIORITY.number,
        });
    }
    // Number words of ten and above become numerals.
    for (const m of text.matchAll(NUMBER_WORD_RUN)) {
        const words = [m[1], m[2]].filter(Boolean);
        const value = parseNumberWords(words);
        if (value === null || value < 10)
            continue;
        // "twenty" alone followed by another number word was already consumed by
        // the two-word branch, so a bare tens value here is correct as it stands.
        edits.push({
            start: m.index,
            end: m.index + m[0].length,
            replacement: matchCase(m[0], String(value)),
            rule: 'number:word-to-digit',
            category: 'number',
            confidence: 'certain',
            note: 'the guide writes ten and above as numerals',
            priority: PRIORITY.number,
        });
    }
    return { edits, flags };
}
function collectFlags(text) {
    const flags = [];
    for (const { word, reason } of SPELLING_FLAGS) {
        const pattern = new RegExp(String.raw `\b${word}\b`, 'gi');
        for (const m of text.matchAll(pattern)) {
            flags.push({
                rule: `flag:${word}`,
                category: 'spelling',
                text: m[0],
                start: m.index,
                end: m.index + m[0].length,
                reason,
            });
        }
    }
    return flags;
}
/** Apply Beam's Australian house style to a block of prose. */
export function applyAustralianStyle(input, options = {}) {
    const opts = { ...DEFAULT_OPTIONS, ...options };
    let working = input;
    let map = IDENTITY_MAP;
    const changes = [];
    // Stage one: dictation cleanup, so later passes see punctuation and decimals
    // rather than the words the speaker used for them.
    if (opts.dictation) {
        const dictationEdits = findDictationFixes(input)
            .filter((h) => opts.applyLikely || h.confidence === 'certain')
            .map((h) => ({
            start: h.start,
            end: h.end,
            replacement: h.replacement,
            rule: `dictation:${h.rule}`,
            category: 'punctuation',
            confidence: h.confidence,
            ...(h.note ? { note: h.note } : {}),
            priority: PRIORITY.punctuation,
        }));
        const result = applyEdits(input, dictationEdits);
        for (const e of result.applied) {
            if (input.slice(e.start, e.end) === e.replacement)
                continue;
            changes.push({
                rule: e.rule,
                category: e.category,
                before: input.slice(e.start, e.end),
                after: e.replacement,
                start: e.start,
                end: e.end,
                confidence: e.confidence,
                ...(e.note ? { note: e.note } : {}),
            });
        }
        working = result.text;
        map = new OffsetMap(result.applied, input.length);
    }
    // Stage two: everything else, all matched against the same text.
    const edits = [];
    for (const h of findPunctuation(working)) {
        edits.push({
            start: h.start, end: h.end, replacement: h.replacement,
            rule: h.rule, category: 'punctuation', confidence: h.confidence,
            ...(h.note ? { note: h.note } : {}),
            priority: PRIORITY.punctuation,
        });
    }
    if (opts.normaliseDates) {
        for (const h of findDates(working)) {
            edits.push({
                start: h.start, end: h.end, replacement: h.replacement,
                rule: 'date:australian-long-form', category: 'date', confidence: h.confidence,
                ...(h.note ? { note: h.note } : {}),
                priority: PRIORITY.date,
            });
        }
        for (const h of findTimes(working)) {
            edits.push({
                start: h.start, end: h.end, replacement: h.replacement,
                rule: 'time:australian', category: 'time', confidence: h.confidence,
                priority: PRIORITY.time,
            });
        }
    }
    for (const h of findMeasures(working, opts.tightenUnits)) {
        const category = h.kind === 'currency' ? 'currency'
            : h.kind === 'phone' ? 'phone'
                : h.kind === 'fraction' ? 'fraction'
                    : 'measure';
        edits.push({
            start: h.start, end: h.end, replacement: h.replacement,
            rule: `${h.kind}:australian`, category, confidence: h.confidence,
            ...(h.note ? { note: h.note } : {}),
            priority: PRIORITY[category],
        });
    }
    edits.push(...collectVocabulary(working, opts.extraVocabulary));
    edits.push(...collectSpelling(working));
    const flags = [];
    if (opts.numberWords) {
        const numbers = collectNumbers(working);
        edits.push(...numbers.edits);
        flags.push(...numbers.flags);
    }
    flags.push(...collectFlags(working));
    const usable = edits.filter((e) => opts.applyLikely || e.confidence === 'certain');
    const deferred = edits.filter((e) => !opts.applyLikely && e.confidence === 'likely');
    const result = applyEdits(working, usable);
    for (const e of result.applied) {
        // Some passes emit an edit purely to claim a span, for example a date that
        // is already in the right form. Nothing changed, so nothing is reported.
        if (working.slice(e.start, e.end) === e.replacement)
            continue;
        const [start, end] = map.rangeToOriginal(e.start, e.end);
        changes.push({
            rule: e.rule,
            category: e.category,
            before: working.slice(e.start, e.end),
            after: e.replacement,
            start,
            end,
            confidence: e.confidence,
            ...(e.note ? { note: e.note } : {}),
        });
    }
    for (const e of deferred) {
        const [start, end] = map.rangeToOriginal(e.start, e.end);
        flags.push({
            rule: e.rule,
            category: e.category,
            text: working.slice(e.start, e.end),
            start,
            end,
            reason: e.note ?? `suggested change to "${e.replacement}", not applied automatically`,
        });
    }
    const mappedFlags = flags.map((f) => {
        const [start, end] = map.rangeToOriginal(f.start, f.end);
        return { ...f, start, end };
    });
    return {
        text: result.text,
        changes: changes.sort((a, b) => a.start - b.start),
        flags: mappedFlags.sort((a, b) => a.start - b.start),
    };
}
export function applyToSegments(segments, base = {}) {
    return segments.map(({ key, text, options }) => {
        const result = applyAustralianStyle(text, { ...base, ...options });
        return { key, text: result.text, changes: result.changes, flags: result.flags };
    });
}
/** A one line summary for a review panel. */
export function summariseChanges(changes) {
    if (changes.length === 0)
        return 'No style changes.';
    const counts = new Map();
    for (const c of changes)
        counts.set(c.category, (counts.get(c.category) ?? 0) + 1);
    const parts = [...counts.entries()]
        .sort((a, b) => b[1] - a[1])
        .map(([category, n]) => `${n} ${category}${n === 1 ? '' : 's'}`);
    const likely = changes.filter((c) => c.confidence === 'likely').length;
    const tail = likely > 0 ? `. ${likely} worth checking` : '';
    return `${changes.length} change${changes.length === 1 ? '' : 's'}: ${parts.join(', ')}${tail}.`;
}
//# sourceMappingURL=index.js.map